import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { useEffect, useId, useState } from "react";

import { TooltipSide } from "@components/constants";

/**
 * Info icon button with a hover/focus tooltip.
 *
 * Use this instead of the frontera-ui-components `Tooltip`: that component
 * wraps @material-tailwind's Tooltip, which needs a Material Tailwind
 * ThemeProvider that basecamp does not mount — so it renders the icon but
 * never shows the popup.
 *
 * Keyboard and pointer accessible (WCAG 1.4.13, 2.4.7, 4.1.2): a named
 * button with a visible focus ring, text wired via aria-describedby, Esc
 * dismisses, and the popup stays open while the pointer moves onto it.
 */
export const InfoTooltip: React.FC<{
  text: string;
  iconClassName?: string;
  label?: string;
  /** TOP when the popup would otherwise cover the control it explains. */
  side?: TooltipSide;
}> = ({
  text,
  iconClassName = "h-3.5 w-3.5 text-text-tertiary",
  label = "More information",
  side = TooltipSide.BOTTOM,
}) => {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const open = (hovered || focused) && !dismissed;
  const tooltipId = useId();

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setDismissed(true);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  return (
    <span
      className="relative inline-flex"
      onMouseEnter={() => {
        setHovered(true);
        setDismissed(false);
      }}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        type="button"
        aria-label={label}
        aria-describedby={tooltipId}
        // Enter/Space fire click on a native button, so this also reopens
        // from the keyboard after Esc.
        onClick={() => setDismissed(false)}
        onFocus={() => {
          setFocused(true);
          setDismissed(false);
        }}
        onBlur={() => setFocused(false)}
        className="inline-flex rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-rio-600 focus-visible:ring-offset-1"
      >
        <InformationCircleIcon className={iconClassName} aria-hidden="true" />
      </button>
      {/* Padding (not margin) keeps the gap inside the hover area, so moving
          the pointer from the icon onto the popup doesn't close it. */}
      <span
        role="tooltip"
        id={tooltipId}
        hidden={!open}
        className={`absolute left-1/2 z-50 w-64 -translate-x-1/2 ${side === TooltipSide.TOP ? "bottom-full pb-1" : "top-full pt-1"}`}
      >
        <span className="block rounded-md bg-slate-800 px-2.5 py-1.5 text-xs font-normal normal-case tracking-normal leading-snug text-white shadow-lg">
          {text}
        </span>
      </span>
    </span>
  );
};

/**
 * Pure-CSS hover/focus tooltip wrapping arbitrary inline
 * content — for truncated text whose full value should appear on hover
 * (native `title` tooltips are slow and easy to miss).
 *
 * Opens ABOVE the trigger: these tooltips can be tall, and a downward
 * one near the page bottom extends the document's scroll height, which
 * shifts the trigger out from under the cursor and flickers the tooltip
 * open/closed in a loop. Upward overflow never grows the scroll area.
 */
export const HoverTooltip: React.FC<{
  text: string;
  className?: string;
  children: React.ReactNode;
}> = ({ text, className, children }) => (
  <span
    tabIndex={0}
    className={`group relative inline-flex min-w-0 cursor-default select-none focus:outline-none ${className ?? ""}`}
  >
    {children}
    <span
      role="tooltip"
      className="pointer-events-none absolute bottom-full left-0 z-50 mb-1 hidden max-w-md whitespace-normal break-words rounded-md bg-slate-800 px-2.5 py-1.5 text-xs font-normal normal-case tracking-normal leading-snug text-white shadow-lg group-hover:block group-focus:block"
    >
      {text}
    </span>
  </span>
);
